(function () {
  'use strict';

  const AUTO_LOAD_LIMIT = 2;
  const LOADER_SRC = 'https://cdn.shopify.com/s/files/1/0420/2905/6153/files/loading.gif?v=1684125578';

  class CollectionInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.grid = container.querySelector('#product-grid');
      this.paginationWrapper = container.querySelector('.pagination-wrapper');
      this.sectionId = this.grid ? this.grid.dataset.id : null;
      this.currentPage = Number(new URLSearchParams(window.location.search).get('page')) || 1;
      this.autoLoadedPages = 0;
      this.loading = false;
      this.observer = null;
      this.sentinel = null;
      this.loader = null;
      this.loadMoreWrapper = null;
      this.loadMoreButton = null;
    }

    init() {
      if (!this.grid || !this.sectionId || !this.paginationWrapper) {
        this.container.classList.remove('infinite-scroll-active');
        return;
      }
      if (!this.pageLinkExists(this.paginationWrapper, this.currentPage + 1)) {
        this.container.classList.remove('infinite-scroll-active');
        return;
      }

      this.container.classList.add('infinite-scroll-active');

      this.sentinel = document.createElement('div');
      this.sentinel.className = 'infinite-scroll__sentinel';
      // Dawn's base.css sets div:empty to display:none, which would leave the
      // sentinel boxless and the IntersectionObserver permanently silent.
      this.sentinel.style.display = 'block';
      this.grid.insertAdjacentElement('afterend', this.sentinel);

      this.loader = document.createElement('div');
      this.loader.className = 'infinite-scroll__loader';
      this.loader.hidden = true;
      const image = document.createElement('img');
      image.src = LOADER_SRC;
      image.alt = '';
      image.width = 64;
      image.height = 64;
      this.loader.appendChild(image);
      this.sentinel.insertAdjacentElement('afterend', this.loader);

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.onSentinelVisible();
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    onSentinelVisible() {
      if (this.loading) return;
      if (this.autoLoadedPages >= AUTO_LOAD_LIMIT) {
        this.stopObserving();
        this.showLoadMoreButton();
        return;
      }
      this.autoLoadedPages += 1;
      this.loadNextPage(false);
    }

    loadNextPage(fromButton) {
      if (this.loading) return;
      this.loading = true;
      this.loader.hidden = false;
      if (this.loadMoreButton) this.loadMoreButton.disabled = true;

      const nextPage = this.currentPage + 1;
      fetch(this.buildPageUrl(nextPage))
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = doc.querySelectorAll('#product-grid > li');
          if (items.length === 0) {
            this.teardown();
            return;
          }

          window.requestAnimationFrame(() => {
            items.forEach((item) => {
              // Dawn's animations.js only observes items present at DOMContentLoaded;
              // without stripping these, appended cards stay invisible.
              item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
              item.removeAttribute('data-cascade');
              this.grid.appendChild(item);
            });
          });

          this.currentPage = nextPage;
          this.updateHistory();

          if (!this.pageLinkExists(doc, nextPage + 1)) {
            this.teardown();
            return;
          }
          if (!fromButton && this.autoLoadedPages >= AUTO_LOAD_LIMIT) {
            this.stopObserving();
            this.showLoadMoreButton();
          }
        })
        .catch(() => this.abort())
        .finally(() => {
          this.loading = false;
          if (this.loader) this.loader.hidden = true;
          if (this.loadMoreButton) this.loadMoreButton.disabled = false;
        });
    }

    buildPageUrl(page) {
      const params = new URLSearchParams();
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.indexOf('filter.') === 0 || key === 'sort_by') params.append(key, value);
      });
      params.set('page', page);
      params.set('section_id', this.sectionId);
      return `${window.location.pathname}?${params.toString()}`;
    }

    updateHistory() {
      const params = new URLSearchParams(window.location.search);
      params.set('page', this.currentPage);
      history.replaceState(history.state, '', `${window.location.pathname}?${params.toString()}`);
    }

    pageLinkExists(root, page) {
      const links = root.querySelectorAll('.pagination-wrapper a[href], a.pagination__item[href]');
      return Array.from(links).some((link) => {
        const href = link.getAttribute('href') || '';
        const params = new URLSearchParams(href.split('?')[1] || '');
        return Number(params.get('page')) === page;
      });
    }

    showLoadMoreButton() {
      if (this.loadMoreWrapper) return;
      this.loadMoreWrapper = document.createElement('div');
      this.loadMoreWrapper.className = 'infinite-scroll__load-more';
      this.loadMoreButton = document.createElement('button');
      this.loadMoreButton.type = 'button';
      this.loadMoreButton.className = 'button';
      this.loadMoreButton.textContent = this.grid.dataset.loadMoreLabel || 'Load more';
      this.loadMoreButton.addEventListener('click', () => this.loadNextPage(true));
      this.loadMoreWrapper.appendChild(this.loadMoreButton);
      this.loader.insertAdjacentElement('afterend', this.loadMoreWrapper);
    }

    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    teardown() {
      this.stopObserving();
      if (this.sentinel) {
        this.sentinel.remove();
        this.sentinel = null;
      }
      if (this.loadMoreWrapper) {
        this.loadMoreWrapper.remove();
        this.loadMoreWrapper = null;
        this.loadMoreButton = null;
      }
      if (this.loader) this.loader.hidden = true;
    }

    // On fetch failure: remove all infinite-scroll UI and unhide the
    // server-rendered pagination so the page degrades to standard paging.
    abort() {
      this.teardown();
      if (this.loader) {
        this.loader.remove();
        this.loader = null;
      }
      this.container.classList.remove('infinite-scroll-active');
    }
  }

  let instance = null;

  function initInstance() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    instance = new CollectionInfiniteScroll(container);
    instance.init();
  }

  function setup() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    initInstance();

    // facets.js replaces this container's contents on every filter/sort
    // change, wiping the sentinel — rebuild from the fresh markup and URL.
    new MutationObserver(() => {
      if (instance) instance.teardown();
      initInstance();
    }).observe(container, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
